import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getAuth } from '../staffAuthApi';
import { app } from '../appAuth';
import { verifyEmailWithToken, sendCustomVerificationEmail } from '../api/emailVerificationApi';
import { VerifyEmailView } from './VerifyEmailView';
import './VerifyEmailPage.css';
import './VerifyEmailView.css';

function getParameterByName(name, url = typeof window !== 'undefined' ? window.location.href : '') {
  const parsed = name.replace(/[[\]]/g, '\\$&');
  const regex = new RegExp(`[?&]${parsed}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}

const DEFAULT_SUCCESS_COPY =
  'Your email has been verified successfully. You may now close this window and sign in to your account.';

export function VerifyEmailPageContent({
  loginPath = '/app/login',
  pageClassName = 'verify-email-page',
  successCopy = DEFAULT_SUCCESS_COPY,
}) {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState('loading');
  const [title, setTitle] = useState('');
  const [copy, setCopy] = useState('');
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState('success');
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const auth = getAuth(app);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const token = searchParams.get('token') || getParameterByName('token');
    const success = searchParams.get('success') || getParameterByName('success');

    if (success === 'true') {
      setState('success');
      setTitle('Email verified');
      setCopy(successCopy);
      return;
    }

    if (token) {
      verifyEmailWithToken(token)
        .then(() => {
          if (!cancelled) {
            setState('success');
            setTitle('Email verified');
            setCopy(successCopy);
          }
        })
        .catch((err) => {
          if (!cancelled) {
            setState('error');
            setTitle('Verification failed');
            setCopy(err?.message || 'Invalid or expired verification link. Please request a new verification email.');
          }
        });
      return;
    }

    setState('prompt');
    setTitle('Verify your email');
    setCopy('We sent a verification link to your email. Click the link in that message to verify your address. If you don’t see it, check your spam folder.');
  }, [searchParams, successCopy]);

  useEffect(() => {
    if (resendCooldownSeconds <= 0) return;
    const t = setInterval(() => setResendCooldownSeconds((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldownSeconds]);

  const handleResend = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) {
      setMessage('Please sign in to resend the verification email.');
      setMessageType('error');
      return;
    }
    setResendLoading(true);
    setMessage(null);
    try {
      const idToken = await user.getIdToken();
      await sendCustomVerificationEmail(idToken);
      setResendCooldownSeconds(120);
      setMessage('Verification email sent. Check your inbox (and spam folder).');
      setMessageType('success');
    } catch (err) {
      const sec = err?.retryAfterSeconds;
      if (typeof sec === 'number' && sec > 0) {
        setResendCooldownSeconds(sec);
        setMessage(`Please wait ${sec} seconds before requesting another email.`);
      } else {
        setMessage(err?.message || 'Failed to send. Try again in a minute.');
      }
      setMessageType('error');
    } finally {
      setResendLoading(false);
    }
  }, [auth]);

  const goToLogin = useCallback(() => {
    if (typeof window !== 'undefined' && window.opener) {
      try {
        window.close();
        return;
      } catch (_) {}
    }
    navigate(loginPath, { replace: true });
  }, [loginPath, navigate]);

  const showSuccessLogin =
    state === 'success'
      ? {
          primaryActionLabel: 'Go to login',
          onPrimaryAction: goToLogin,
        }
      : {};

  return (
    <div className={pageClassName}>
      <div className="verify-email-page__card">
        <div className="verify-email-page__header">
          <img
            src="/neatschedule.png"
            alt="Neat Schedule"
            className="verify-email-page__logo"
          />
          {state === 'loading' && (
            <p className="verify-email-page__sub">
              We’re verifying your email to keep your account secure.
            </p>
          )}
        </div>
        <VerifyEmailView
          state={state}
          title={title}
          copy={copy}
          message={message}
          messageType={messageType}
          resendCooldownSeconds={resendCooldownSeconds}
          resendLoading={resendLoading}
          onResend={handleResend}
          primaryActionLabel={
            showSuccessLogin.primaryActionLabel
            || (state === 'error' ? 'Go to login' : undefined)
          }
          onPrimaryAction={
            showSuccessLogin.onPrimaryAction
            || (state === 'error' ? goToLogin : undefined)
          }
          secondaryActionLabel={
            state === 'error'
              ? 'Try again'
              : state === 'prompt'
                ? 'Back to login'
                : undefined
          }
          onSecondaryAction={
            state === 'error'
              ? () => window.location.reload()
              : state === 'prompt'
                ? goToLogin
                : undefined
          }
        />
      </div>
    </div>
  );
}

export function VerifyEmailPage() {
  return <VerifyEmailPageContent loginPath="/app/login" />;
}

export default VerifyEmailPage;
